import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeaderNav } from '../components/HeaderNav';
import { roadmapApi } from '../services/api';
import {
  ROADMAP_SCHEMA_VERSION,
  type Roadmap,
  type SpineCapability,
  type SpineGroup,
  type SpineProduct,
  type SpineSocket,
} from '../roadmapTypes';

/**
 * THE SPINE — what ADP is, in one screen.
 *
 * WHY THIS PAGE EXISTS, WHICH IS A CRITIQUE AND NOT A FEATURE REQUEST
 *
 * The previous roadmap page rendered the five readings of ADP work as a lane strip over a
 * filterable list of disagreements. Leadership reviewed it and the verdict was that it had
 * "zero graphic visualization that is helpful", that it was "an abstraction of relationships
 * that lack any visual narrative", and — the part that actually stung — that "the content
 * and goal of the Platform Capabilities is absent".
 *
 * That last one was literally true. `ADP-PLATFORM-OVERVIEW.md` §4, §5 and §6 are each
 * TITLED "Capability". They hold twelve of them, in prose, with the numbers that back them
 * sitting one section away in §7. The page surfaced none of it. It had read the document's
 * tables and skipped the document's argument, which is a good description of what an
 * instrument does and a bad description of what a reader needs.
 *
 * THE FORM: one spine, the products riding it, the things that finish it.
 *
 * It is the overview's own central metaphor — §3, "the spine — one runtime, built once" —
 * drawn instead of described. Capabilities stack ABOVE the backbone because they are what
 * the runtime provides. Products plug in BELOW it because they consume it. The four
 * unbuilt vertebrae sit at the right-hand end of the backbone because they are the visible
 * distance still to travel, and the whole thing reads left to right as the sentence §11
 * already wrote: *a runtime that works ──▶ a runtime that governs*.
 *
 * Nothing on this page is authored here. Every string comes from the generator, which reads
 * it out of the document. A hand-written capability list on a page about claim-versus-truth
 * would be a second source of truth, drifting away from the first, on the page whose entire
 * subject is exactly that.
 *
 * THE MECHANICS ARE HERE, AND THEY ARE INVISIBLE
 *
 * The lane engine did not go away and did not get weaker. `roadmap_build.py` still joins
 * §7's numbers to `NORTH-STAR.md` §3's recount commands, still emits every disagreement,
 * and now also REFUSES TO WRITE AT ALL if a capability stops joining to its numbers. What
 * changed is what a reader sees of it:
 *
 *   solid border    every number under this capability is recountable from source, and the
 *                   recount agrees
 *   outlined        a claim with no counted number behind it, or one whose recount is
 *                   missing or disagrees
 *
 * One bit. The reader is owed *can this be checked*; the taxonomy of why it could not be is
 * the engineering view, and putting that on this page is precisely what the last revision
 * did wrong. The full instrument still exists, one click away at `/roadmap/detail`.
 *
 * THE WORDS `lane`, `gap` and `unmeasured` DO NOT APPEAR ON THIS PAGE, and that is gated,
 * not merely intended — `viz-src/check-spine.py` greps the rendered DOM for them and fails.
 * It is the one rule here most likely to erode by accident, because those three words are
 * how everyone who works on this code thinks about it.
 *
 * ON COLOURS AND CONTRAST — inherited from RoadmapPage.tsx, where all of it was measured:
 * opacity modifiers on the semantic tokens emit NO CSS RULE at all (`bg-error/10` renders
 * transparent, silently), so every tint here is a solid `*-bg` token. And a tint is not a
 * licence for its own foreground: `text-warning` on `bg-warning-bg` measured 1.82:1. Chip
 * and card text is theme text; the border and the tint carry the signal. `check-spine.py`
 * gates WCAG AA on this page's `<main>` in both themes.
 */

/** §4/§5/§6 in document order, given the short shouted label the spine diagram uses. */
const GROUP_LABEL: Record<number, string> = {
  4: 'MISSION RUNTIME',
  5: 'GOVERNANCE',
  6: 'EXECUTION',
};

/**
 * A group's label, falling back to its own title rather than to a blank or to "OTHER".
 *
 * The generator discovers capability groups BY SHAPE, so a §13 group would start appearing
 * here with no code change — and would otherwise arrive with no label at all. Deriving one
 * from the document's own heading is the behaviour that degrades correctly; a lookup miss
 * rendering as empty space is the behaviour that silently loses a whole column.
 */
function groupLabel(g: SpineGroup): string {
  return GROUP_LABEL[g.heading] ?? g.title.toUpperCase();
}

/** Socket appearance. Three states, three appearances — see the `cited` note below. */
const SOCKET_STYLE: Record<SpineSocket['state'], string> = {
  live: 'bg-success-bg border-success',
  inherited: 'bg-info-bg border-info',
  pending: 'bg-tower-surface border-dashed border-tower-border',
};

const SOCKET_WORD: Record<SpineSocket['state'], string> = {
  live: 'running today',
  inherited: 'comes for free',
  pending: 'still to do',
};

/**
 * One capability card. The tint IS the mechanics, and it is the only trace of them.
 *
 * `title` carries the difference a single bit cannot: which number is unrecounted, or that
 * there is no number at all. That is on purpose — the distinction belongs somewhere, and a
 * tooltip is somewhere without being in the way of the story.
 */
function Capability({ cap }: { cap: SpineCapability }) {
  const proven = cap.evidence === 'proven';
  return (
    <div
      data-capability={cap.id}
      data-evidence={cap.evidence}
      title={
        proven
          ? `${cap.metrics.map((m) => `${m.label} ${m.value}`).join(', ')} — counted from source and re-counted independently`
          : cap.metrics.length === 0
            ? 'Stated in the platform overview. No counted number stands behind it yet.'
            : `Stated, but ${cap.unrecounted.join(', ')} has no independent re-count behind it yet.`
      }
      className={`rounded-lg p-3 h-full border ${
        proven
          ? 'bg-tower-surface border-tower-border'
          : // Outlined, not tinted red. An uncounted claim is not a defect — most of these
            // are capabilities nobody has thought to put a number on. Colouring it as a
            // failure would be a louder statement than the data supports.
            'bg-tower-bg border-dashed border-tower-border'
      }`}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm font-semibold text-tower-text leading-snug">{cap.title}</span>
        {cap.metrics.map((m) => (
          <span
            key={m.label}
            data-metric={m.label}
            className={`text-[11px] font-mono px-1.5 py-0.5 rounded border text-tower-text ${
              m.agrees ? 'bg-success-bg border-success' : 'bg-tower-surface border-dashed border-tower-border'
            }`}
          >
            <span className="font-bold">{m.value}</span>{' '}
            <span className="opacity-80">{m.label.toLowerCase()}</span>
          </span>
        ))}
      </div>
      <p className="text-xs text-tower-muted mt-1.5 leading-relaxed">{cap.detail}</p>
    </div>
  );
}

/** One product cartridge, with its four sockets into the spine. */
function Product({ p }: { p: SpineProduct }) {
  return (
    <div
      data-product={p.id}
      className="rounded-lg border border-tower-border bg-tower-surface p-4 flex flex-col"
    >
      {/* The plug. A short stem drawn up toward the backbone, so the cards read as
          attached to it rather than merely positioned under it. */}
      <div className="h-4 w-px bg-tower-border self-center -mt-6 mb-2" aria-hidden="true" />
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-base font-bold text-tower-text">{p.name}</span>
        <span className="text-[11px] font-mono text-tower-muted">
          {p.attached}/{p.sockets_total}
        </span>
      </div>
      <div className="text-xs text-tower-text font-semibold mt-0.5">{p.distance}</div>

      <div className="flex gap-1 mt-3" role="list" aria-label={`${p.name} — what is plugged in`}>
        {p.sockets.map((s) => (
          <div
            key={s.label}
            role="listitem"
            data-socket={s.label}
            data-socket-state={s.state}
            data-socket-cited={String(s.cited)}
            // `cited: false` is drawn at lower opacity than a stated "still to do". The
            // document being SILENT about a socket and the document SAYING it is unbuilt
            // are different facts, and a bar that renders them identically would be
            // asserting something nobody wrote down — in the reassuring direction, which
            // is the direction this whole page exists to catch.
            title={
              s.quote
                ? `${s.label} — ${SOCKET_WORD[s.state]} ("${s.quote}")`
                : `${s.label} — the platform overview does not say`
            }
            className={`flex-1 h-2 rounded-sm border ${SOCKET_STYLE[s.state]} ${
              s.cited ? '' : 'opacity-40'
            }`}
          />
        ))}
      </div>
      <div className="flex gap-1 mt-1">
        {p.sockets.map((s) => (
          <div key={s.label} className="flex-1 text-[9px] text-tower-muted leading-tight text-center">
            {s.label}
          </div>
        ))}
      </div>

      <p className="text-xs text-tower-muted mt-3 leading-relaxed flex-1">
        <span className="text-tower-text font-medium">{p.lead}</span> {p.detail}
      </p>
    </div>
  );
}

export function SpinePage() {
  const [data, setData] = useState<Roadmap | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    roadmapApi
      .get()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, []);

  const spine = data?.spine;

  return (
    <div className="min-h-screen bg-tower-bg text-tower-text flex flex-col">
      <header className="h-14 bg-tower-surface border-b border-tower-border flex items-center px-6 gap-4 shrink-0">
        <Link
          to="/"
          className="text-lg font-mono font-bold text-tower-accent tracking-wider hover:text-tower-text transition-colors"
        >
          MAESTRO
        </Link>
        <div className="h-4 w-px bg-tower-border" />
        <span className="text-sm text-tower-accent font-semibold">Roadmap</span>
        <div className="flex-1" />
        <HeaderNav />
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto">
          {error && (
            <div className="p-4 mb-4 bg-error-bg border border-error rounded-lg text-sm text-tower-text">
              Could not load the roadmap ({error}). This route requires a signed-in session —
              an authentication failure and an outage look the same from here, so check{' '}
              <span className="font-mono">Sign out / Sign in</span> before assuming the API is down.
            </div>
          )}

          {!data && !error && <div className="text-sm text-tower-muted">Loading…</div>}

          {data && data.schemaVersion !== ROADMAP_SCHEMA_VERSION && (
            <div className="p-4 mb-4 bg-warning-bg border border-warning rounded-lg text-sm text-tower-text">
              This page renders roadmap schema v{ROADMAP_SCHEMA_VERSION}; the API returned v
              {data.schemaVersion}. Fields added since may not be shown — the page is not
              pretending otherwise.
            </div>
          )}

          {/* A payload with no spine renders as a NAMED absence, never as a platform with no
              capabilities. An empty three-column grid is indistinguishable from a true
              answer, and the true answer it resembles is the worst one. */}
          {data && !spine && (
            <div className="p-4 mb-4 bg-warning-bg border border-warning rounded-lg text-sm text-tower-text">
              This roadmap payload predates the capability story — it carries the underlying
              readings but not the capabilities themselves. That is a stale artifact, not a
              platform without capabilities.{' '}
              <Link to="/roadmap/detail" className="underline">
                The detailed view
              </Link>{' '}
              still works.
            </div>
          )}

          {spine && (
            <>
              {/* ── the thesis ──────────────────────────────────────────────── */}
              <h1 className="text-2xl font-bold text-tower-accent mb-2">One runtime, built once</h1>
              {spine.arc && (
                <p className="text-lg text-tower-text mb-1 font-medium">
                  From <span className="font-bold">{spine.arc.from}</span>
                  <span className="mx-3 font-mono text-tower-muted" aria-hidden="true">
                    ──▶
                  </span>
                  to <span className="font-bold">{spine.arc.to}</span>.
                </p>
              )}
              <p className="text-sm text-tower-muted mb-6 max-w-4xl leading-relaxed">
                {spine.summary.capabilities} capabilities are built once and shared.{' '}
                {spine.summary.products} products ride them. {spine.summary.remaining} pieces
                of platform work close the distance — each one unblocks all {spine.summary.products} products
                at once, and none of it is per-product screens.
              </p>

              {/* ── capabilities: what the runtime provides ─────────────────── */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {spine.groups.map((g) => (
                  <div key={g.id} data-group={g.id} className="flex flex-col">
                    <div className="text-[11px] font-mono font-bold tracking-widest text-tower-text mb-2">
                      {groupLabel(g)}
                    </div>
                    <div className="flex flex-col gap-2">
                      {g.capabilities.map((c) => (
                        <Capability key={c.id} cap={c} />
                      ))}
                    </div>
                    {/* Feeds INTO the backbone. Without it the three columns merely sit
                        above a horizontal rule, which is what the first render looked
                        like — a divider, not a spine. The stem is what makes the bar
                        below read as the thing they all rest on. */}
                    <div className="flex-1 min-h-[12px]" />
                    <div className="w-px flex-none h-3 bg-tower-border self-center" aria-hidden="true" />
                  </div>
                ))}
              </div>

              {/* ── THE BACKBONE ───────────────────────────────────────────────
                  One solid bar for what is built, then four hollow vertebrae for what is
                  not. The notches are deliberately the same height as the bar and
                  deliberately empty, and they carry the same numbers as the list further
                  down: the distance to done is DRAWN, to scale, beside what is finished,
                  rather than described in a sentence underneath it. */}
              <div className="flex items-stretch gap-1.5 h-12">
                <div className="flex-1 rounded-l-md bg-primary-bg border-2 border-tower-accent flex items-center justify-center px-4">
                  <span className="text-xs font-mono font-bold tracking-[0.2em] text-tower-text">
                    THE SPINE · SHARED, BUILT ONCE
                  </span>
                </div>
                {spine.remaining.map((r, i) => (
                  <div
                    key={r.id}
                    data-vertebra={r.id}
                    title={r.title}
                    className="w-14 rounded-sm border-2 border-dashed border-tower-border bg-tower-bg flex items-center justify-center"
                  >
                    <span className="text-xs font-mono font-bold text-tower-muted">{i + 1}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-1 mb-2">
                <span className="text-[11px] text-tower-muted font-mono">
                  {spine.remaining.length} still to build &rarr;
                </span>
              </div>

              {/* ── products: what rides it ──────────────────────────────────
                  Stems again, this time hanging DOWN from the backbone: capabilities are
                  what the runtime provides, products are what consume it, and the
                  above/below split is the whole reason this is a spine drawing rather
                  than three stacked panels. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {spine.products.map((p) => (
                  <Product key={p.id} p={p} />
                ))}
              </div>

              {/* ── the four that finish it ────────────────────────────────── */}
              <div className="rounded-lg border border-tower-border bg-tower-surface p-5 mb-6">
                <h2 className="text-base font-bold text-tower-text mb-1">
                  What finishes the spine
                </h2>
                <p className="text-xs text-tower-muted mb-4">
                  Platform work, not product work. Each one unblocks all{' '}
                  {spine.summary.products} products at once.
                </p>
                <ol className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {spine.remaining.map((r, i) => (
                    <li
                      key={r.id}
                      data-remaining={r.id}
                      className="rounded-lg border border-dashed border-tower-border bg-tower-bg p-3 flex gap-3"
                    >
                      <span className="text-lg font-mono font-bold text-tower-muted shrink-0 leading-none pt-0.5">
                        {i + 1}
                      </span>
                      <span>
                        <span className="text-sm font-semibold text-tower-text">{r.title}</span>
                        {r.detail && (
                          <span className="block text-xs text-tower-muted mt-1 leading-relaxed">
                            {r.detail}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* ── the legend, in the reader's vocabulary and not ours ────── */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-tower-muted border-t border-tower-border pt-4">
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 rounded border border-tower-border bg-tower-surface" />
                  counted from source, and independently re-counted
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 rounded border border-dashed border-tower-border bg-tower-bg" />
                  stated, with no count behind it yet
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 rounded border border-success bg-success-bg" />
                  running today
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 rounded border border-info bg-info-bg" />
                  inherited from the spine
                </span>
                <span className="flex-1" />
                <Link
                  to="/roadmap/detail"
                  className="text-tower-muted hover:text-tower-accent underline"
                >
                  Where the numbers come from &rarr;
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
